// @ts-strict-ignore
import React, { useCallback, useMemo } from 'react'
import { UseFormSetValue } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import { Contact } from '@node-space/types'
import { Checkbox } from '@node-space/ui-components'
import { UNITED_STATES } from 'constants/contacts'
import { useFeatureFlags } from 'hooks/useFeatureFlags'
import { ContactFiatInstrumentFormData } from 'pages/Contacts/schemas/contactFiatInstrumentSchema'
import { EEA_COUNTRY_CODES } from 'pages/Contacts/utils/constants.fiatInstruments'
import { BeneficiaryDetailsCurrency, PaymentMethods } from 'types/beneficiaries'
import type { Address } from 'types/contacts'

interface BeneficiaryAddressCopyProps {
  formValues: ContactFiatInstrumentFormData
  contact: Contact
  setValue: UseFormSetValue<ContactFiatInstrumentFormData>
}

export const BeneficiaryAddressCopy = ({
  formValues,
  contact,
  setValue,
}: BeneficiaryAddressCopyProps) => {
  const { t } = useTranslation()
  const { enableHybridAddress } = useFeatureFlags()

  const formattedContactAddress = useMemo(() => {
    const { addressLine1, addressLine2, city, region, postCode, country } =
      contact?.entity?.address ?? {}
    return [addressLine1, addressLine2, city, region, postCode, country].filter(Boolean).join(', ')
  }, [contact?.entity?.address])

  const copyAddressCountryOnly = useMemo(() => {
    const { paymentMethods, currency, accountNumber } = formValues || {}

    const isSwift = paymentMethods?.includes(PaymentMethods.SWIFT)
    const isEurAndCountryIsNonEea =
      currency === BeneficiaryDetailsCurrency.EUR &&
      !EEA_COUNTRY_CODES.includes(accountNumber?.slice(0, 2).toUpperCase() ?? '')
    const isUsdFedWireOrFednow =
      paymentMethods?.includes(PaymentMethods.FEDWIRE) ||
      paymentMethods?.includes(PaymentMethods.FEDNOW)

    if (enableHybridAddress || isSwift || isUsdFedWireOrFednow || isEurAndCountryIsNonEea)
      return false

    return true
  }, [
    formValues?.paymentMethods,
    formValues?.currency,
    formValues?.accountNumber,
    enableHybridAddress,
  ])

  const checkboxLabel = useMemo(() => {
    return copyAddressCountryOnly
      ? t('contacts.sameAddressCountryAsContact')
      : t('contacts.sameAddressAsContact')
  }, [t, copyAddressCountryOnly])

  const checkboxDescription = useMemo(() => {
    return copyAddressCountryOnly ? contact?.entity?.address?.country : formattedContactAddress
  }, [copyAddressCountryOnly, contact, formattedContactAddress])

  const handleSameAsContactAddressChange = useCallback(
    (isSame: boolean) => {
      setValue('sameAsContactAddress', isSame)

      if (copyAddressCountryOnly) {
        setValue('beneficiaryCountryCode', isSame ? contact?.entity?.address?.country : '')
        setValue('beneficiaryCountryName', isSame ? contact?.entity?.address?.country : '')
        return
      }

      setValue(
        'beneficiaryAddressLine1',
        isSame ? (contact?.entity?.address?.addressLine1 ?? '') : ''
      )
      setValue(
        'beneficiaryAddressLine2',
        isSame ? (contact?.entity?.address?.addressLine2 ?? '') : ''
      )
      setValue('beneficiaryCity', isSame ? (contact?.entity?.address?.city ?? '') : '')
      setValue('beneficiaryRegion', isSame ? (contact?.entity?.address?.region ?? '') : '')
      setValue(
        'beneficiaryRegionCode',
        isSame && contact?.entity?.address?.country === UNITED_STATES
          ? ((contact?.entity?.address as Address)?.stateCode ?? '')
          : ''
      )
      setValue('beneficiaryPostCode', isSame ? (contact?.entity?.address?.postCode ?? '') : '')
      setValue('beneficiaryCountryCode', isSame ? (contact?.entity?.address?.country ?? '') : '')
      setValue('beneficiaryCountryName', isSame ? (contact?.entity?.address?.country ?? '') : '')
    },
    [contact, setValue, copyAddressCountryOnly]
  )

  return (
    <Checkbox
      id="beneficiary-address-copy-details-checkbox"
      name="beneficiary-address-copy-details-checkbox"
      testId="beneficiary-address-copy-details-checkbox"
      onCheckedChange={handleSameAsContactAddressChange}
      label={checkboxLabel}
      checked={formValues?.sameAsContactAddress}
    >
      {checkboxDescription}
    </Checkbox>
  )
}
